// useRehabBudgetingFeature.ts

/**
 * This hook fetches the rehab budget via the GraphQL query
 * and merges each local
 * line item definition with the user's actual data (budget, memo, selection).
 */

import { useEffect, useMemo, useState } from "react"
import {
  useEditRehab,
  useRehabBudget,
  useSetInitialBudgetPropertyType,
  useUpdateBudgetPropertyDetails,
  useUpdatePrimaryDrawsContact,
} from "@/queries/rehab"
import type {
  BudgetLineItemType,
  CombinedLoanAndProductsQuery,
} from "@/generated/graphql"
import { useBoolVariation } from "@/hooks/launchDarklyHooks"
import { useStore } from "@nanostores/react"
import { $ldContextString, $ldKey } from "@/services/launchDarkly"
import {
  BudgetReviewStatus,
  getRehabCategories,
} from "@/screens/rehab/utils/Rehab"
import { type NormalizedLineItem } from "@/types/rehabTypes"
import { stringToNumber } from "@/utils/NumberUtils"
import { queryClient } from "@/queries/client"

export const useRehabBudgetingFeature = (
  loanApplication: NonNullable<CombinedLoanAndProductsQuery["loanApplication"]>,
  isDesktop: boolean,
  rehabBudgetId?: string | undefined
) => {
  // LaunchDarkly feature-flag
  const featureIsEnabled = useBoolVariation("rehab-budgeting-feature", false)

  // We may want to elevate this to all features in the future
  const ldUserKey = useStore($ldKey)

  // Debugging
  const userDebugString = useStore($ldContextString)

  // Create local state for the rehabBudget id.
  const [localRehabBudgetId, setLocalRehabBudgetId] = useState<
    string | undefined
  >(rehabBudgetId || loanApplication.rehabBudget?.id)

  // Fetch the real rehab budget using the loan application
  const {
    data: rehabData,
    isLoading,
    isPending,
    refetch,
    isRefetching,
    error,
  } = useRehabBudget(localRehabBudgetId, loanApplication.id, featureIsEnabled)

  // When a new rehabBudget id is available, update local state and invalidate related queries.
  useEffect(() => {
    if (rehabData?.rehabBudget?.id) {
      setLocalRehabBudgetId(rehabData.rehabBudget.id)
      void queryClient.invalidateQueries({ queryKey: ["loanApplication"] })
      void queryClient.invalidateQueries({ queryKey: ["loans"] })
    }
  }, [rehabData?.rehabBudget?.id])

  const rehabBudget = rehabData?.rehabBudget

  // To update the overall rehabBudget
  const { mutate: editRehabDetails, isPending: isSavingRehab } = useEditRehab()
  const { mutate: updatePropertyDetails, isPending: isSavingProperty } =
    useUpdateBudgetPropertyDetails()

  const {
    mutate: setInitialBudgetPropertyType,
    isPending: isSavingInitialPropertyType,
  } = useSetInitialBudgetPropertyType()

  const { mutate: updatePrimaryDrawsContact, isPending: isSavingContact } =
    useUpdatePrimaryDrawsContact()

  const isSaving: boolean =
    isSavingRehab ||
    isSavingProperty ||
    isSavingInitialPropertyType ||
    isSavingContact

  // Normalize the fetched data into a friendly format
  const apiLineItemsMap = useMemo(() => {
    const items = rehabBudget?.lineItems
    if (!items) return {}

    return items.reduce<Record<string, BudgetLineItemType>>((acc, item) => {
      if (item?.category) {
        acc[item.category] = item
      }
      return acc
    }, {})
  }, [rehabData])

  // Retrieve the local definitions
  const localDefs = useMemo(() => getRehabCategories(isDesktop), [isDesktop])

  // Merge into an array of NormalizedLineItems
  const lineItemsWithUserData: NormalizedLineItem[] = useMemo(() => {
    return Object.entries(localDefs).map(([_, localDef]) => {
      // e.g. localDef.fieldName might be "foundation", "demo", etc.
      // Use that to find the matching real line item from the dictionary
      const userItem = apiLineItemsMap[localDef.fieldName]

      // Convert user’s selection to a string array
      let userSelections: string[] = []
      if (userItem?.selection) {
        userSelections = Array.isArray(userItem.selection)
          ? userItem.selection
          : [userItem.selection]
      }

      return {
        ...localDef, // includes label, icon, etc.
        // Real data from the backend, defaulting if not present
        budget: userItem?.budget ?? 0,
        memo: userItem?.memo ?? "",
        userSelections,
        apiCategory: userItem?.category ?? localDef.fieldName,
      }
    })
  }, [localDefs, apiLineItemsMap])

  const totalBudget = stringToNumber(rehabBudget?.subtotal || "0")
  const isGCInfoMissing =
    !rehabBudget?.primaryDrawsContact?.name ||
    !rehabBudget?.primaryDrawsContact?.email

  const isDetailsTabDisabled = totalBudget === 0 // Disable the Details tab if totalBudget === 0
  const isSummaryTabDisabled = totalBudget === 0 || isGCInfoMissing // Disable the Summary tab if totalBudget === 0 or GC info is missing.

  const loanPropertyData = loanApplication?.property || {}
  const existingPropertyType =
    rehabBudget?.initialPropertyDetails?.propertyType ||
    loanPropertyData.propertyType ||
    null

  const latestPropertyData = useMemo(() => {
    const { modifiedPropertyDetails, initialPropertyDetails } =
      rehabBudget || {}

    const normalizedModifiedBathrooms: number | null =
      Number(modifiedPropertyDetails?.propertyBaths) !== 0
        ? Number(modifiedPropertyDetails?.propertyBaths)
        : null

    const normalizedInitialBathrooms: number | null =
      Number(initialPropertyDetails?.propertyBaths) !== 0
        ? Number(initialPropertyDetails?.propertyBaths)
        : null

    return {
      squareFootage:
        modifiedPropertyDetails?.propertySqFt ??
        initialPropertyDetails?.propertySqFt ??
        loanPropertyData.area ??
        0,
      bedroomCount:
        modifiedPropertyDetails?.propertyBeds ??
        initialPropertyDetails?.propertyBeds ??
        loanPropertyData.bedrooms ??
        0,
      bathroomCount:
        normalizedModifiedBathrooms ??
        normalizedInitialBathrooms ??
        loanPropertyData.bathrooms ??
        0,
      propertyType:
        modifiedPropertyDetails?.propertyType ??
        initialPropertyDetails?.propertyType ??
        loanPropertyData.propertyType ??
        "N/A",
    }
  }, [
    rehabBudget?.modifiedPropertyDetails, // Ensure it updates with new data
    rehabBudget?.initialPropertyDetails,
    loanApplication,
  ])

  const isEditable =
    rehabBudget?.status === BudgetReviewStatus.INCOMPLETE ||
    rehabBudget?.status === BudgetReviewStatus.DRAFT ||
    rehabBudget?.status === BudgetReviewStatus.MORE_INFO_NEEDED

  return {
    featureIsEnabled,
    ldUserKey,
    userDebugString,
    lineItemsWithUserData, // final array for UI
    editRehabDetails,
    setInitialBudgetPropertyType,
    updatePropertyDetails,
    updatePrimaryDrawsContact,
    existingPropertyType,
    latestPropertyData,
    isDetailsTabDisabled,
    isSummaryTabDisabled,
    isLoading,
    isPending,
    isSaving,
    refetch,
    isRefetching,
    isEditable,
    error,
    rehabBudgetId: localRehabBudgetId,
    rehabBudget,
  }
}
